import type { QueryClient } from '@tanstack/react-query';
import type { Encounter } from '@/features/encounters/api';
import { api } from '@/lib/api';

/**
 * RL-1836 review: one notes save at a time per encounter, so an autosave, a
 * save on leaving and Sign/Close never send the same version twice.
 */
const inflight = new Map<string, Promise<unknown>>();

/** Resolves when a notes save already on its way for this encounter ends. */
export function notesSettled(id: string): Promise<void> {
  return (inflight.get(id) ?? Promise.resolve()).then(
    () => undefined,
    () => undefined,
  );
}

export function trackNotesSave<T>(id: string, p: Promise<T>): Promise<T> {
  inflight.set(id, p);
  p.finally(() => {
    if (inflight.get(id) === p) inflight.delete(id);
  }).catch(() => undefined);
  return p;
}

/**
 * Leaving the consultation (another page, a visit form, closing the tab):
 * the typed notes go into the cached encounter at once, so coming back shows
 * them, and are sent (keepalive) with the version current when it is their
 * turn.
 */
export function flushNotes(qc: QueryClient, id: string, notes: string) {
  const key = ['encounter', id];
  const cached = qc.getQueryData<Encounter>(key);
  if (!cached || cached.notes === notes) return;
  qc.setQueryData<Encounter>(key, { ...cached, notes });
  void trackNotesSave(
    id,
    notesSettled(id)
      .then(() =>
        api.patch<Encounter>(
          `/encounters/${id}/notes`,
          {
            version: qc.getQueryData<Encounter>(key)?.version ?? cached.version,
            notes,
          },
          { keepalive: true },
        ),
      )
      .then((r) => qc.setQueryData(key, r.data)),
  ).catch(() => undefined);
}
